import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { WritePost } from './WritePost';
import {
  APPROVE_SOCIAL_POST,
  channelMark,
  channelName,
  dayTime,
  postText,
  postTime,
  REACH_CALENDAR,
  REACH_CHANNELS,
  ReachPost,
  RETRY_SOCIAL_POST,
  SKIP_SOCIAL_POST,
  time,
} from './reach';

/**
 * Reach → Calendar ("tinycms UX optimization review v6", Prototype v6): what
 * is waiting on the person first (approve or skip each post), then the week —
 * posted, scheduled and waiting, colour-coded. An agent's post on a channel set
 * to "Ask me first" waits here and can't go out until approved. "Write a post"
 * lets the person post by hand (WritePost.tsx).
 */

const DAY_MS = 86400000;
const TONE: Record<string, { dot: string; border: string; label: string }> = {
  posted: { dot: 'var(--ink-green)', border: 'var(--ink-line)', label: 'Posted' },
  publishing: { dot: 'var(--ink-green)', border: 'var(--ink-line)', label: 'Posting' },
  scheduled: { dot: 'var(--ink-accent)', border: 'var(--ink-line)', label: 'Scheduled' },
  waiting: { dot: 'var(--ink-amber)', border: 'var(--ink-amber)', label: 'Waiting on you' },
  // A post whose time passed without going out (Prototype v7: "Didn't post").
  missed: { dot: 'var(--ink-red)', border: 'var(--ink-red)', label: 'Didn’t post' },
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

export function ReachCalendar() {
  const { accountId } = useAccount();
  const [offset, setOffset] = useState(0);
  const start = useMemo(() => {
    const today = startOfDay(new Date());
    return new Date(today.getTime() + offset * 7 * DAY_MS);
  }, [offset]);
  const end = useMemo(() => new Date(start.getTime() + 7 * DAY_MS), [start]);

  const { data, loading, refetch } = useQuery<{
    reachCalendar: { posts: ReachPost[]; waiting: ReachPost[] };
  }>(REACH_CALENDAR, {
    variables: { accountId, from: start.toISOString(), to: end.toISOString() },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: channelData } = useQuery<{
    socialProfiles: Array<{ id: string; isActive: boolean }>;
  }>(REACH_CHANNELS, { variables: { accountId }, skip: !accountId });
  const [approve] = useMutation(APPROVE_SOCIAL_POST);
  const [skip] = useMutation(SKIP_SOCIAL_POST);
  const [retry] = useMutation(RETRY_SOCIAL_POST);
  // The post opened in the side panel (any post on the week can be opened).
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);

  const posts = useMemo(
    () =>
      (data?.reachCalendar.posts || []).filter(
        (p) => p.reachStatus !== 'skipped' && p.reachStatus !== 'draft'
      ),
    [data]
  );
  const waiting = data?.reachCalendar.waiting || [];
  const openPost = posts.find((p) => p.id === openId) || null;
  const connected = (channelData?.socialProfiles || []).filter((c) => c.isActive).length;

  const counts = useMemo(() => {
    const c = { posted: 0, scheduled: 0, waiting: 0, missed: 0 };
    for (const p of posts) {
      if (p.reachStatus === 'posted' || p.reachStatus === 'publishing') c.posted += 1;
      else if (p.reachStatus === 'scheduled') c.scheduled += 1;
      else if (p.reachStatus === 'waiting') c.waiting += 1;
      else if (p.reachStatus === 'missed') c.missed += 1;
    }
    return c;
  }, [posts]);

  const days = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => {
        const day = new Date(start.getTime() + i * DAY_MS);
        const next = new Date(day.getTime() + DAY_MS);
        return {
          day,
          posts: posts
            .filter((p) => {
              const t = postTime(p);
              return t && t >= day && t < next;
            })
            .sort((a, b) => (postTime(a)?.getTime() || 0) - (postTime(b)?.getTime() || 0)),
        };
      }),
    [start, posts]
  );

  const act = async (post: ReachPost, kind: 'approve' | 'skip' | 'retry') => {
    setBusy(post.id);
    try {
      if (kind === 'retry') {
        await retry({ variables: { id: post.id } });
        showInkToast('Trying again. It goes out in a minute.');
        setOpenId(null);
      } else if (kind === 'approve') {
        await approve({ variables: { id: post.id } });
        const t = postTime(post);
        showInkToast(
          t && t.getTime() > Date.now()
            ? `Approved. It goes out ${dayTime(t)}.`
            : 'Approved. It goes out in a minute.'
        );
        setOpenId(null);
      } else {
        await skip({ variables: { id: post.id } });
        showInkToast('Skipped. It won’t go out.');
        setOpenId(null);
      }
      await refetch();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const range = `${start.toLocaleDateString([], { month: 'short', day: 'numeric' })} – ${new Date(
    end.getTime() - DAY_MS
  ).toLocaleDateString([], {
    month: start.getMonth() === new Date(end.getTime() - DAY_MS).getMonth() ? undefined : 'short',
    day: 'numeric',
  })}`;
  const todayKey = startOfDay(new Date()).getTime();

  return (
    <div className="rc">
      <div className="rc-bar">
        <span className="ink-muted">
          {counts.posted} posted · {counts.scheduled} scheduled · {waiting.length} waiting
          {counts.missed ? ` · ${counts.missed} didn’t post` : ''}
          {connected ? (
            <>
              {' · '}
              <button type="button" className="ink-quiet-link" onClick={() => setWriting(true)}>
                Write a post
              </button>
            </>
          ) : null}
        </span>
        <span className="rc-nav">
          <button type="button" aria-label="Previous week" onClick={() => setOffset((o) => o - 1)}>
            ‹
          </button>
          <span className="rc-range">{range}</span>
          <button type="button" aria-label="Next week" onClick={() => setOffset((o) => o + 1)}>
            ›
          </button>
        </span>
      </div>

      {connected === 0 && channelData ? (
        <div className="rc-empty">
          <div className="rc-empty-title">No channels yet</div>
          <p>
            Connect X, Threads, Instagram or another channel, and your agent can draft posts for it
            from what you publish.
          </p>
          <Link to="/reach/channels" className="ink-btn ink-btn-primary">
            Connect a channel
          </Link>
        </div>
      ) : null}

      {waiting.length ? (
        <section className="rc-need">
          <div className="rc-need-head">
            <span className="ink-dot" style={{ background: 'var(--ink-amber)' }} />
            <strong>
              {waiting.length} {waiting.length === 1 ? 'thing needs' : 'things need'} you
            </strong>
            <span className="ink-muted">Nothing goes out until you approve it.</span>
          </div>
          {waiting.map((p) => {
            const t = postTime(p);
            return (
              <div className="rc-need-row" key={p.id}>
                <span className="rc-mark" title={channelName(p.platform)}>
                  {channelMark(p.platform)}
                </span>
                <div className="rc-need-text">
                  <div className="rc-need-title">{postText(p) || 'Untitled post'}</div>
                  <div className="rc-need-meta">
                    {channelName(p.platform)}
                    {t
                      ? ` · ${dayTime(t)}${t.getTime() < Date.now() ? ' (passed — goes out when approved)' : ''}`
                      : ''}
                  </div>
                </div>
                <button
                  type="button"
                  className="ink-btn ink-btn-quiet"
                  disabled={busy === p.id}
                  onClick={() => void act(p, 'skip')}
                >
                  Skip
                </button>
                <button
                  type="button"
                  className="ink-btn ink-btn-primary"
                  disabled={busy === p.id}
                  onClick={() => void act(p, 'approve')}
                >
                  Approve
                </button>
              </div>
            );
          })}
        </section>
      ) : null}

      <div className="rc-week" aria-busy={loading && !data}>
        {days.map(({ day, posts: list }) => {
          const isToday = day.getTime() === todayKey;
          return (
            <div className="rc-day" key={day.getTime()}>
              <div className="rc-day-head">
                <span>
                  {day.toLocaleDateString([], { weekday: 'short' })} <b>{day.getDate()}</b>
                </span>
                {isToday ? <span className="rc-today">TODAY</span> : null}
              </div>
              {list.map((p) => {
                const tone = TONE[p.reachStatus] || TONE.scheduled;
                const t = postTime(p);
                return (
                  <button
                    type="button"
                    className="rc-post"
                    key={p.id}
                    style={{ borderColor: tone.border }}
                    title={tone.label}
                    onClick={() => setOpenId(p.id)}
                  >
                    <div className="rc-post-head">
                      <span className="ink-dot" style={{ background: tone.dot }} />
                      <span className="rc-post-time">{t ? time(t) : ''}</span>
                      <span className="rc-mark rc-mark-sm">{channelMark(p.platform)}</span>
                    </div>
                    <div className="rc-post-text">{postText(p)}</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      <div className="rc-legend">
        {(['posted', 'scheduled', 'waiting', 'missed'] as const).map((k) => (
          <span key={k}>
            <span className="ink-dot" style={{ background: TONE[k].dot }} />
            {TONE[k].label}
          </span>
        ))}
      </div>
      {openPost ? (
        <PostPanel
          post={openPost}
          busy={busy === openPost.id}
          onClose={() => setOpenId(null)}
          onAct={(kind) => void act(openPost, kind)}
        />
      ) : null}
      {writing ? (
        <WritePost
          onClose={() => setWriting(false)}
          onPosted={() => {
            setWriting(false);
            void refetch();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * One post, opened from the week (Prototype v7): what it says, when, and — for
 * one that didn't go out — why, with Try again / Skip. A waiting post can be
 * approved or skipped here too.
 */
function PostPanel({
  post,
  busy,
  onClose,
  onAct,
}: {
  post: ReachPost;
  busy: boolean;
  onClose: () => void;
  onAct: (kind: 'approve' | 'skip' | 'retry') => void;
}) {
  const tone = TONE[post.reachStatus] || TONE.scheduled;
  const t = postTime(post);
  const channel = channelName(post.platform);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const missed = post.reachStatus === 'missed';
  const waiting = post.reachStatus === 'waiting';
  return (
    <>
      <div className="rc-panel-back" onClick={onClose} />
      <aside className="rc-panel" role="dialog" aria-label={`${channel} post`}>
        <div className="rc-panel-head">
          <span className="rc-mark">{channelMark(post.platform)}</span>
          <div className="rc-panel-title">
            <strong>{channel}</strong>
            <span className="rc-panel-status">
              <span className="ink-dot" style={{ background: tone.dot }} />
              <span style={{ color: missed ? 'var(--ink-red)' : undefined }}>{tone.label}</span>
              {t ? <span className="ink-muted"> · {dayTime(t)}</span> : null}
            </span>
          </div>
          <button type="button" className="rc-panel-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="rc-panel-body">
          <div className="rc-panel-post">{postText(post) || 'Untitled post'}</div>
          {missed ? (
            <p className="rc-panel-why">
              {channel} didn’t take this post{t ? ` at ${time(t)}` : ''}
              {post.failure ? `: ${post.failure}` : ''}. Nothing was posted.
            </p>
          ) : null}
        </div>
        {missed || waiting ? (
          <div className="rc-panel-foot">
            <button type="button" className="ink-btn" disabled={busy} onClick={() => onAct('skip')}>
              Skip
            </button>
            <button
              type="button"
              className="ink-btn ink-btn-primary"
              disabled={busy}
              onClick={() => onAct(missed ? 'retry' : 'approve')}
            >
              {missed ? 'Try again' : 'Approve'}
            </button>
          </div>
        ) : null}
      </aside>
    </>
  );
}
